import { useCallback } from 'react';
import { merge } from 'lodash';
import { useMeQuery } from '@frameio/graphql-client/generated';
import { useIsUserIdentified } from '@frameio/segment/contexts';
import { enqueueOrRun } from '@frameio/segment-ot';
import { emitEvent } from '@frameio/telemetry';
import { useRefBox } from '@frameio/vapor-migration/hooks/useRefBox';
import { getDevFlag } from '../../dev-flags';
import { useCurrentPageType } from '../../routing';
import type {
  TrackingEventName,
  TrackingEventProperties,
} from './trackingEvents.generated';
import { useCoreOptionsForAnalytics } from './useCoreOptionsForAnalytics';

/* A dictionary of options. For example, enable or disable specific destinations. */
type TrackOptions = Record<string, any>;

/**
 * True when an event's props object has at least one required key.
 * `{} extends T` is true only when every key of T is optional, so this lets us
 * make the `props` argument required exactly when the event demands properties.
 *
 * The empty-object type `{}` is intentional and load-bearing here: it is the
 * "every key optional" probe, not a stand-in for `object` or `unknown`.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type HasRequiredProps<T> = {} extends T ? false : true;

/**
 * Trailing arguments for a `track` call, specialised per event. Events with no
 * required properties may omit `props` entirely; events with required props
 * must pass them.
 */
type TrackArgs<E extends TrackingEventName> = HasRequiredProps<
  TrackingEventProperties[E]
> extends true
  ? [
      props: TrackingEventProperties[E],
      options?: TrackOptions,
      callback?: () => void,
    ]
  : [
      props?: TrackingEventProperties[E],
      options?: TrackOptions,
      callback?: () => void,
    ];

/**
 * Strongly-typed track function: the allowed `props` shape is derived from the
 * event name via the generated `TrackingEventProperties` map.
 */
export type TrackFn = <E extends TrackingEventName>(
  eventName: E,
  ...args: TrackArgs<E>
) => void;

/**
 * Untyped escape hatch for `track`. Accepts any event name and props, bypassing
 * the generated tracking-plan types. Use only for call sites that don't yet
 * match the plan; prefer the typed `useTrack` everywhere it compiles.
 */
export type UnsafeTrackFn = (
  eventName: string,
  props?: Record<string, unknown>,
  options?: TrackOptions,
  callback?: () => void
) => void;

const isSegmentDebuggerEnabled = getDevFlag('SEGMENT_DEBUGGER');

/**
 * Generic tracking function.
 * @param eventName - Event name.
 * @param logToDataDog - Whether to log the tracking event to Datadog as well.
 * @returns {Function} a generated track function with core properties and options already included, wrapped in `segment-ot` enqueueOrRun helper.
 */
// export function useTrack(eventName: TrackingEventName, logToDataDog?: boolean) {
export function useTrack(defaultProperties: Record<string, any> = {}) {
  const { data: user } = useMeQuery({
    context: {
      // Avoid redirecting to login on this call (e.g. shares)
      shouldRedirectToLoginIfUnauthenticated: false,
      bypassShareAuth: true,
    },
    fetchPolicy: 'cache-only',
  });

  const isEdu =
    user?.me?.__typename === 'AuthenticatedUser'
      ? user.me.isEdu || false
      : false;

  const { coreOptions } = useCoreOptionsForAnalytics(isEdu);
  const { isUserIdentified } = useIsUserIdentified();
  const pageType = useCurrentPageType();
  const skipQueue =
    pageType === 'authenticating' ||
    pageType === 'shareReviewFinder' ||
    pageType === 'shareReviewReel' ||
    pageType === 'uploadLinkPublic' ||
    pageType === 'unauthenticated' ||
    pageType === 'exportPicker';

  const defaults = useRefBox(defaultProperties);
  // The implementation is intentionally type-erased: a single body cannot be
  // verified against `TrackFn`'s per-event generic signature, so it is typed
  // loosely here and exposed as `TrackFn` via the assertion on return.
  const track = useCallback(
    (
      eventName: TrackingEventName,
      props: Record<string, unknown> = {},
      options: TrackOptions = {},
      callback?: () => void
    ) => {
      /**
       * Note: passing any overriding options should be a limited use case,
       * `useCoreOptionsForAnalytics` should be the canonical source.
       */
      const allOptions = merge({}, coreOptions(), options);

      const properties = { ...defaults.current, ...props };

      /**
       * Logs event to the console in development
       * if the `isSegmentDebuggerEnabled` dev flag is enabled.
       */
      if (isSegmentDebuggerEnabled) {
        // eslint-disable-next-line no-console
        console.log(
          '%c%s - %c%s %o \n',
          'padding: 1px; font-weight: bold; color: #52bd95;',
          'Segment Tracking Event',
          'padding: 1px; font-weight: bold; color:#5B53FF',
          eventName,
          {
            properties,
            options: allOptions,
          }
        );
      }

      /**
       * If the consumer has said to do so, log this event to DataDog as well.
       */
      if (options?.logToDatadog) {
        emitEvent('segmentEvent', { eventName, ...props });
      }

      return enqueueOrRun({
        event: () => {
          window?.analytics?.track(eventName, properties, allOptions, () => {
            callback?.();
          });
        },
        enqueueEvent: !isUserIdentified && !skipQueue,
      });
    },
    [coreOptions, isUserIdentified, skipQueue, defaults]
  );

  return track as unknown as TrackFn;
}

/**
 * `useTrack` without tracking-plan type enforcement. Returns a `track` function
 * that accepts any event name and props. Intended as a temporary escape hatch
 * for call sites that predate, or diverge from, the generated tracking plan.
 */
export function useTrackUnsafe(
  defaultProperties: Record<string, any> = {}
): UnsafeTrackFn {
  return useTrack(defaultProperties) as unknown as UnsafeTrackFn;
}
